import { SettingsValueProps } from '../components/settings/types';
import { CheckEnvironment, SupportedEnvironments } from '../utils/environment';

declare global {
  interface Window {
    BRAND_TITLE: string;
    locale: string;
  }
}

export const domainBVR = 'guest.beachviewrentals.com';
export const domainMRR = 'guest.mauiresortrentals.com';

export const QA_DOMAIN = 'qa.guest-portal-v2.pages.dev';
export const QA_DOMAIN_SUFFIX = '.guest-portal-v2.pages.dev';

export enum BrandCode {
  BVR = 'BVR',
  DRS = 'DRS',
  MRR = 'MRR',
}

export interface PromoCode {
  id: string;
  link: string;
  sortDescription: string;
  description: string;
  label: string;
  background?: string;
  inverted?: boolean;
  character?: string;
}

export interface BrandConfig {
  name: string;
  greeting: string;
  thankyou: string;
  brandId: string;
  brandCode: BrandCode;
  backgroundUrl?: string;
  logoUrl: string;
  loginLogo: string;
  iconUrl: string;
  logoUrlDark: string;
  iconUrlDark: string;
  /** Folder under public/favicon holding this brand's favicon set (e.g. 'mrr', 'bvr'). */
  faviconFolder: string;
  /** Path to this brand's web app manifest. */
  manifestUrl: string;
  themeSettings: SettingsValueProps;
  salesforceEmbeddedMessaging: {
    orgId: string;
    channelName: string;
    siteUrl: string;
    scrt2Url: string;
    language: string;
    /**
     * Salesforce loads a page with an active session's conversation window maximized unless this
     * is true. Defaults to true here so an in-progress chat stays minimized on a new page load.
     */
    shouldMinimizeWindowOnNewTab?: boolean;
  };
  customCssUrl?: string; // Optional, for custom styles if needed
  address: {
    streetAddress: string;
    city: string;
    state: string;
    postalCode: string;
  };
  website: string;
  supportEmail: string;
  supportPhone: string;
  /** Optional SMS-capable support number. When set, a "Text" contact option is shown. */
  supportText?: string;
  locale: string;
  timezone: string;
  timezoneCode: string;
  stripePublishableKey: string;
  travelInsuranceEnabled: boolean;
  allowCheckInRequests: boolean;
  helpLinks: Record<string, string>; // Links for guest registration and ID verification
  promoCodes?: PromoCode[];
}

// Define your branding configurations for different domains here
const domainBrand: Record<string, BrandConfig> = {
  [domainBVR]: {
    name: 'Beachview Rentals',
    greeting: 'Welcome',
    thankyou: 'Thank you',
    brandId: 'beach_view_rentals',
    brandCode: BrandCode.BVR,
    backgroundUrl: '/assets/background/bg_img3.webp',
    logoUrl: '/logo/bvr_logo_horizontal.svg',
    loginLogo: '/logo/bvr_logo_horizontal.svg',
    iconUrl: '/logo/bvr_logo_small.svg',
    logoUrlDark: '/logo/bvr_logo_horizontal_dark.svg',
    iconUrlDark: '/logo/bvr_logo_small_dark.svg',
    faviconFolder: 'bvr',
    manifestUrl: '/manifest-bvr.json',
    website: 'https://www.beachviewrentals.com',
    supportEmail: 'rentals@beachviewrentals.com',
    supportPhone: '(949) 673-2627',
    supportText: '(949) 368-7343',
    locale: 'Newport Beach',
    timezone: 'America/Los_Angeles',
    timezoneCode: 'PT',
    address: {
      streetAddress: '2210 Newport Blvd. Suite 200',
      city: 'Newport Beach',
      state: 'CA',
      postalCode: '92663',
    },
    themeSettings: {
      themeMode: 'light',
      themeDirection: 'ltr',
      themeContrast: 'default',
      themeLayout: 'vertical',
      themeColorPresets: 'default',
      themeStretch: false,
    },
    salesforceEmbeddedMessaging: {
      channelName: 'BVR_Chat_Guest_Portal',
      orgId: process.env.REACT_APP_SF_ORG_ID as string,
      siteUrl: process.env.REACT_APP_SF_MESSAGING_SITE_BVR as string,
      scrt2Url: process.env.REACT_APP_SF_SCRT_URL as string,
      language: 'en_US',
    },
    stripePublishableKey: process.env.REACT_APP_BVR_STRIPE_PUBLISHABLE_KEY as string,
    travelInsuranceEnabled: true,
    allowCheckInRequests: false,
    helpLinks: {
      guest_registration:
        'https://drive.google.com/file/d/1bvtU9tamfyFMObstPgYkKo09UI7rJIU-/preview',
      guest_id_verification:
        'https://drive.google.com/file/d/1CIstEOxZCv4AifL5ZNy16qhRqLWgJbTB/preview',
      beach_and_baby_gear: 'https://www.beachviewrentals.com/beach-and-baby-gear-rentals',
      privacy_policy: 'https://www.beachviewrentals.com/additional-information/privacy-policy',
    },
  },
  [domainMRR]: {
    name: 'Maui Resort Rentals',
    greeting: 'Aloha',
    thankyou: 'Mahalo',
    brandId: 'maui_resort_rentals',
    brandCode: BrandCode.MRR,
    backgroundUrl: '/assets/background/bg_img1_33pct.jpg',
    logoUrl: '/logo/mrr_logo_horizontal.svg',
    loginLogo: '/logo/mrr_logo_horizontal.svg',
    iconUrl: '/logo/mrr_logo_small.svg',
    logoUrlDark: '/logo/mrr_logo_horizontal_dark.svg',
    iconUrlDark: '/logo/mrr_logo_small_dark.svg',
    faviconFolder: 'mrr',
    manifestUrl: '/manifest.json',
    website: 'https://www.mauiresortrentals.com',
    supportEmail: 'experience@mauiresortrentals.com',
    supportPhone: '(888) 468-0578',
    // supportText: '(888) 468-0578',
    locale: 'Maui',
    timezone: 'Pacific/Honolulu',
    timezoneCode: 'HT',
    address: {
      streetAddress: '10 Halawai Dr #A-3',
      city: 'Lahaina',
      state: 'HI',
      postalCode: '96761',
    },
    themeSettings: {
      themeMode: 'light',
      themeDirection: 'ltr',
      themeContrast: 'default',
      themeLayout: 'vertical',
      themeColorPresets: 'default',
      themeStretch: false,
    },
    salesforceEmbeddedMessaging: {
      channelName: 'MRR_Chat_Guest_Portal',
      orgId: process.env.REACT_APP_SF_ORG_ID as string,
      siteUrl: process.env.REACT_APP_SF_MESSAGING_SITE_MRR as string,
      scrt2Url: process.env.REACT_APP_SF_SCRT_URL as string,
      language: 'en_US',
    },
    stripePublishableKey: process.env.REACT_APP_MRR_STRIPE_PUBLISHABLE_KEY as string,
    travelInsuranceEnabled: true,
    allowCheckInRequests: true,
    helpLinks: {
      guest_registration:
        'https://drive.google.com/file/d/1momSK4VJVaerLGdHIMz3tB_dQS5dVaZS/preview',
      guest_id_verification:
        'https://drive.google.com/file/d/1P8Tmu-jpfX1S2dyNHqJ380SNhFmLB1fZ/preview',
      privacy_policy: 'https://www.mauiresortrentals.com/privacy-policy',
    },
    // promoCodes: [
    // {
    //   label: 'Have you booked your flight yet?',
    //   sortDescription: 'Book Hawaiian Airlines Promo',
    //   description:
    //     'Unlock a special discount on your Hawaiian Airlines booking with this exclusive link. It’s our way of saying mahalo for flying with us. But don’t delay—this limited-time offer won’t be around for long!',
    //   link: 'https://www.hawaiianairlines.com/affiliate?affcode=MRREALTY',
    //   // inverted: true,
    //   background: '/assets/background/overlay_3.jpg',
    //   character: '/assets/illustrations/characters/character_1.png',
    //   id: 'hawaiian_airlines_promo',
    // },
    // ],
  },
  default: {
    name: 'Dream Resorts',
    greeting: 'Aloha',
    thankyou: 'Mahalo',
    brandId: 'maui_resort_rentals',
    brandCode: BrandCode.DRS,
    backgroundUrl: '/assets/background/bg_img2.webp',
    logoUrl: '/logo/mrr_logo_horizontal.svg',
    loginLogo: '/logo/mrr_logo_horizontal.svg',
    iconUrl: '/logo/mrr_logo_small.svg',
    logoUrlDark: '/logo/mrr_logo_horizontal_dark.svg',
    iconUrlDark: '/logo/mrr_logo_small_dark.svg',
    faviconFolder: 'mrr',
    manifestUrl: '/manifest.json',
    website: 'https://www.mauiresortrentals.com',
    supportEmail: 'experience@mauiresortrentals.com',
    supportPhone: '(888) 468-0578',
    locale: 'Maui',
    timezone: 'Pacific/Honolulu',
    timezoneCode: 'HT',
    address: {
      streetAddress: '10 Halawai Dr #A-3',
      city: 'Lahaina',
      state: 'HI',
      postalCode: '96761',
    },
    themeSettings: {
      themeMode: 'light',
      themeDirection: 'ltr',
      themeContrast: 'default',
      themeLayout: 'vertical',
      themeColorPresets: 'default',
      themeStretch: false,
    },
    salesforceEmbeddedMessaging: {
      channelName: '',
      orgId: '',
      siteUrl: '',
      scrt2Url: '',
      language: 'en_US',
    },
    stripePublishableKey: process.env.REACT_APP_DRR_STRIPE_PUBLISHABLE_KEY as string,
    travelInsuranceEnabled: true,
    allowCheckInRequests: false,
    helpLinks: {},
  },
};

// Get brand from cookie (inline to avoid circular dependency)
function getBrandFromCookieInline(): string | null {
  // Check if we're in development environment
  if (!CheckEnvironment(SupportedEnvironments.Dev)) {
    return null;
  }

  const cookies = document.cookie.split(';');
  const brandCookie = cookies.find((cookie) => cookie.trim().startsWith('dev-brand-override='));

  if (brandCookie) {
    return decodeURIComponent(brandCookie.split('=')[1]);
  }

  return null;
}

// Determine the current hostname based on the new requirements
function determineCurrentHostname(): string {
  // In development, check for cookie-based brand override first
  if (CheckEnvironment(SupportedEnvironments.Dev)) {
    const cookieBrand = getBrandFromCookieInline();
    if (cookieBrand) {
      return cookieBrand;
    }
    // In dev without brand cookie, default to MRR to prevent Stripe key errors
    // BrandGuard will redirect to brand selector anyway
    console.warn('No brand cookie; defaulting to MRR; will redirect to brand selector');
    return domainMRR;
  }

  const actualHostname = window.location.hostname;

  // If the user's host exactly matches our defined brand URLs, use that
  if (actualHostname === domainBVR || actualHostname === domainMRR) {
    return actualHostname;
  }

  //NOTE: WE MIGHT NEED THIS
  //  if (actualHostname.endsWith('.guest-portal-v2.pages.dev')) {
  //    return actualHostname;
  //  }

  // For production: use actual hostname (no brand cookie logic)
  // For dev environments without brand cookie: use actual hostname
  return actualHostname;
}

const currentHostname = determineCurrentHostname();

// Select the brand configuration based on the current hostname
const brandConfig = domainBrand[currentHostname] || domainBrand.default; // Fallback to 'default' if no match

/**
 * Get all available brand domains (excluding 'default')
 * Useful for development brand selector
 */
export function getAvailableBrands(): string[] {
  return Object.keys(domainBrand).filter((domain) => domain !== 'default');
}

/**
 * Get brand config by domain
 */
export function getBrandConfigByDomain(domain: string): BrandConfig | null {
  return domainBrand[domain] || null;
}

export default brandConfig;
